import { isAuthError } from '@/errors';
import { createLogger } from '@/logger';
import type { ITransport } from '@/types';

const log = createLogger('TransportChain');

/**
 * Tries each transport in priority order and uses the first one that is ready.
 * This keeps the flush logic in EventQueue completely free of "which transport
 * do I use?" decisions.
 *
 * Open/Closed: add a new transport (e.g. Beacon API for unload) by appending
 * to the chain — no existing code changes needed.
 */
export class TransportChain implements ITransport {
  readonly name = 'TransportChain';

  constructor(private readonly transports: ITransport[]) {
    if (transports.length === 0) {
      throw new Error('[TransportChain] Must have at least one transport');
    }
  }

  isReady(): boolean {
    return this.transports.some((t) => t.isReady());
  }

  async send(events: unknown[]): Promise<void> {
    const ready = this.transports.filter((t) => t.isReady());
    if (ready.length === 0) {
      throw new Error('[TransportChain] No transport is ready');
    }
    let lastErr: unknown;
    for (const transport of ready) {
      try {
        await transport.send(events);
        return;
      } catch (err) {
        // Auth errors are terminal — another transport would fail identically.
        // Surface immediately so EventQueue stops the recorder.
        if (isAuthError(err)) throw err;
        lastErr = err;
        log.warn(`Transport ${transport.name} failed, trying next`, err);
      }
    }
    throw lastErr instanceof Error ? lastErr : new Error('[TransportChain] All transports failed');
  }

  dispose(): void {
    for (const transport of this.transports) {
      transport.dispose();
    }
  }
}
